import React, { useEffect, useState } from 'react';
import Button from '../common/Button';
import { getRandomWords } from './words.ts';
import { Check, X } from 'lucide-react';
import { set } from 'firebase/database';
import { updateUserScores } from '../../services/leaderboardService.ts';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { incrementChallengeScore } from '../../services/challengeService';
import { logUserActivity } from '../../services/activityLogService';

interface SynonymActivityProps {
  correctAnswer: string;
  currentWord: object;
  onComplete: () => void;
  setSynonymActivity: (value: boolean) => void;
  setFillBlankActivity: (value: boolean) => void;
  getDayExercise: (activity: string, word: object) => Promise<boolean>;
  saveDayExercise: (activity: string) => void;
  setShowReward: (value: boolean) => void;
  allActivitiesCompleted: boolean;
  setPoints: (value: number) => void;
  onPointsEarned: (points: number) => void;
}

const SynonymActivity: React.FC<SynonymActivityProps> = ({ correctAnswer, onComplete, currentWord, setSynonymActivity, setFillBlankActivity, getDayExercise, saveDayExercise, setShowReward, allActivitiesCompleted, setPoints, onPointsEarned}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [options, setOptions] = useState<string[]>([]);
  const { currentUser } = useAuth();

  const handleOptionSelect = (option: string) => {
    if (hasSubmitted) return;
    setSelectedOption(option);
  };

  const handleSubmit = () => {
    if (!selectedOption || hasSubmitted) return;
    
    const correct = selectedOption === correctAnswer;
    setIsCorrect(correct);
    setHasSubmitted(true);
    saveDayExercise('synonym');
    
    if (correct) {
      let earned = 25;
      if (failedAttempts === 0) earned = 100;
      else if (failedAttempts === 1) earned = 50;
      onPointsEarned(earned);
      setPoints(earned);
      setShowReward(true);

      setTimeout(() => {
        setShowReward(false);
        onComplete();
      }, 1500);
    } else {
      setFailedAttempts(prev => prev + 1);
    }
  };

  const handleReset = () => {
    setSelectedOption(null);
    setHasSubmitted(false);
    setIsCorrect(false);
  };

  function shuffleArray(array) {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1)); // random index from 0 to i
      [array[i], array[j]] = [array[j], array[i]];  // swap elements
    }
    return array;
  }

  const checkExercise = async () => {
    const isCompleted = await getDayExercise('synonym', currentWord);
    if (isCompleted) {
      setHasSubmitted(true);
      setIsCorrect(true);
    } else {
      setHasSubmitted(false);
      setIsCorrect(false);
    }
  }

  useEffect(() => {
    const options = getRandomWords();
    console.log('correctAnswer:', correctAnswer);
    options.push(correctAnswer);
    setOptions(shuffleArray(options));
    checkExercise();
  }, []);

  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100 p-6">
      <h3 className="text-xl font-semibold text-gray-800 mb-4">Synonym Quiz</h3>
      
      <div className="mb-6">
        <p className="text-gray-700">{`Which word means the same or similar to "${currentWord?.word}"?`}</p>
      </div>
      
      <div className="space-y-3 mb-6">
        {options.map((option, index) => (
          <button
            key={index}
            onClick={() => handleOptionSelect(option)}
            className={`w-full text-left p-4 rounded-lg transition-all ${
              selectedOption === option
                ? hasSubmitted
                  ? option === correctAnswer
                    ? 'bg-green-100 border-green-300 border'
                    : 'bg-red-100 border-red-300 border'
                  : 'bg-blue-100 border-blue-300 border'
                : 'bg-gray-50 hover:bg-gray-100 border border-gray-200'
            }`}
            disabled={hasSubmitted}
          >
            <div className="flex justify-between items-center">
              <span>{option}</span>
              {hasSubmitted && option === correctAnswer && (
                <Check className="text-green-600 h-5 w-5" />
              )}
              {hasSubmitted && selectedOption === option && option !== correctAnswer && (
                <X className="text-red-600 h-5 w-5" />
              )}
            </div>
          </button>
        ))}
      </div>
      
      {hasSubmitted ? (
        <div className={`p-4 rounded-lg mb-4 ${isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
          <p className="font-medium">
            {isCorrect 
              ? '✓ Correct! Well done!' 
              : `✗ Incorrect. The correct answer is "${correctAnswer}".`}
          </p>
        </div>
      ) : null}
      
      <div className="flex justify-end space-x-3">
        {hasSubmitted && !isCorrect ? (
          <Button variant="outline" onClick={handleReset}>
            Try Again
          </Button>
        ) : hasSubmitted && isCorrect && !allActivitiesCompleted ? (
          <Button onClick={() => onComplete()}>
            Next Activity
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit} 
            disabled={!selectedOption || hasSubmitted}
          >
            {hasSubmitted  ? 'Completed' : 'Check Answer'}
          </Button>
        )}
        {hasSubmitted && !isCorrect && !allActivitiesCompleted && (
          <Button variant="outline" onClick={() => {
            onPointsEarned(0);
            setSynonymActivity(false);
            setFillBlankActivity(true);
          }}>
            Skip
          </Button>
        )}
      </div>
    </div>
  );
};

export default SynonymActivity;